import { useEffect, useLayoutEffect } from 'react'
import { Navigate, Route, Routes, useLocation, useMatch } from 'react-router'
import { DetailPanel } from '@/components/DetailPanel'
import { EventCard } from '@/components/Events'
import { QuickAdd } from '@/components/QuickAdd'
import { SearchSheet } from '@/components/SearchSheet'
import { Splash } from '@/components/Splash'
import { Toast } from '@/components/Toast'
import { UpdateToast } from '@/components/UpdateToast'
import { WhatsNewAnnouncer } from '@/components/WhatsNew'
import { FeedbackSheet } from '@/components/FeedbackSheet'
import { startPwa } from '@/lib/pwa'
import { usePaths } from '@/hooks/usePaths'
import { useShortcuts } from '@/hooks/useShortcuts'
import { startPush } from '@/lib/push'
import { installTestHooks } from '@/lib/testHooks'
import { AppShell } from '@/shell/AppShell'
import { NameScreen } from '@/screens/NameScreen'
import { SettingsScreen } from '@/screens/SettingsScreen'
import { SignInScreen } from '@/screens/SignInScreen'
import { StarterCategoriesScreen } from '@/screens/StarterCategoriesScreen'
import { WelcomeScreen } from '@/screens/WelcomeScreen'
import { startAuthListener, useAuth } from '@/store/auth'
import { resetCalendarStore } from '@/store/calendar'
import { startCategoryListeners } from '@/store/categories'
import { startHouseholdListener, useHousehold } from '@/store/household'
import { usePrefs, type ViewKey } from '@/store/prefs'
import { startTaskListeners } from '@/store/tasks'
import { setFeedbackOpen, startOnlineListener, useUi } from '@/store/ui'
import { CalendarPage } from '@/views/calendar/CalendarPage'
import { PlanPage } from '@/views/plan/PlanPage'

const VIEW_PATHS: Record<ViewKey, string> = { plan: '/plan', calendar: '/calendar' }

/**
 * The views, kept mounted in one place: `/task/:id` opens the edit panel over
 * the last-used view without rebuilding it, so its week, day, scroll and open
 * groups stay as they were. Notifications deep-link to `/task/:id`.
 */
function Views() {
  const { pathname, state } = useLocation()
  const task = useMatch('/task/:id')
  const lastView = usePrefs((s) => s.view)
  const view: ViewKey | 'settings' | null = task ? lastView : pathname.startsWith('/calendar') ? 'calendar' : pathname.startsWith('/plan') ? 'plan' : pathname.startsWith('/settings') ? 'settings' : null
  const readOnly = !!(state as { readOnly?: boolean } | null)?.readOnly
  // a new tab starts at its top rather than wherever the last one was scrolled; the edit panel keeps the view, and so the place
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [view])
  return (
    <>
      {view === 'plan' && <PlanPage />}
      {view === 'calendar' && <CalendarPage />}
      {view === 'settings' && <SettingsScreen />}
      {task && <DetailPanel key={task.params.id} taskId={task.params.id!} returnTo={VIEW_PATHS[lastView]} readOnly={readOnly} />}
      <Routes>
        <Route path="/" element={<Navigate to="/plan" replace />} />
        {/* addresses from before the Plan tab, kept for installed phones and old links */}
        <Route path="/list" element={<Navigate to="/plan" replace />} />
        <Route path="/board" element={<Navigate to="/plan" replace />} />
        <Route path="/week" element={<Navigate to="/plan" replace />} />
        <Route path="/plan" element={null} />
        <Route path="/calendar" element={null} />
        <Route path="/settings" element={null} />
        <Route path="/task/:id" element={null} />
        <Route path="*" element={<Navigate to="/plan" replace />} />
      </Routes>
    </>
  )
}

/** Remembers the last view per device, and applies the theme override. */
function Housekeeping() {
  const { pathname } = useLocation()
  const setView = usePrefs((s) => s.setView)
  const theme = useHousehold((s) => s.userDoc?.settings.theme ?? 'system')
  useShortcuts()
  useEffect(() => {
    const view = (Object.keys(VIEW_PATHS) as ViewKey[]).find((k) => pathname.startsWith(VIEW_PATHS[k]))
    if (view) setView(view)
  }, [pathname, setView])
  useEffect(() => {
    const root = document.documentElement
    if (theme === 'system') delete root.dataset.theme
    else root.dataset.theme = theme
    // the browser chrome follows the theme in use, not only the system setting
    const metas = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    metas.forEach((m) => {
      const forDark = (m.media ?? '').includes('dark')
      const dark = theme === 'dark' || (theme === 'system' && forDark)
      m.content = dark ? '#131929' : '#F3F5F8'
    })
  }, [theme])
  return null
}

/** Starts the task and category listeners once the household is known, and push on this device. */
function DataListeners() {
  const paths = usePaths()
  useEffect(() => {
    if (!paths.hid || !paths.uid) return
    const stopTasks = startTaskListeners(paths)
    const stopCategories = startCategoryListeners(paths)
    let stopPush: (() => void) | null = null
    startPush(paths.uid).then((stop) => {
      stopPush = stop
    })
    return () => {
      stopTasks()
      stopCategories()
      stopPush?.()
      resetCalendarStore()
    }
  }, [paths.hid, paths.uid]) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

export function App() {
  const authStatus = useAuth((s) => s.status)
  const user = useAuth((s) => s.user)
  const hhStatus = useHousehold((s) => s.status)
  const onboarding = useHousehold((s) => s.onboarding)

  useEffect(() => {
    installTestHooks()
    startPwa()
    const stopOnline = startOnlineListener()
    const stopAuth = startAuthListener()
    return () => {
      stopOnline()
      stopAuth()
    }
  }, [])

  useEffect(() => {
    if (!user) return
    return startHouseholdListener(user)
  }, [user])

  if (authStatus === 'loading') return <Splash />
  if (authStatus === 'out')
    return (
      <>
        <SignInScreen />
        <UpdateToast />
      </>
    )
  if (hhStatus === 'loading') return <Splash />
  if (hhStatus === 'none') return <WelcomeScreen />
  if (onboarding === 'categories') return <StarterCategoriesScreen />
  if (onboarding === 'name') return <NameScreen />

  return (
    <>
      <Housekeeping />
      <DataListeners />
      <AppShell>
        <Views />
        <QuickAdd placement="floating" />
        <SearchSheet />
        <EventCard />
        <Toast />
        <UpdateToast />
        <WhatsNewAnnouncer />
        <GlobalFeedback />
      </AppShell>
    </>
  )
}

/** Send feedback, opened from the More menu or Settings over whatever is showing; its draft lasts the visit. */
function GlobalFeedback() {
  const open = useUi((s) => s.feedbackOpen)
  return <FeedbackSheet open={open} onClose={() => setFeedbackOpen(false)} />
}
